import * as THREE from 'three';

import { EulerOrder } from './core';

export type RotationAxisTriplet = [number, number, number];

const CARTOGRAPHER_TO_THREE_EULER_ORDER: Record<EulerOrder, THREE.EulerOrder> = {
  xyz: 'XYZ',
  xzy: 'XZY',
  yxz: 'YXZ',
  yzx: 'YZX',
  zxy: 'ZXY',
  zyx: 'ZYX',
};

const THREE_TO_CARTOGRAPHER_EULER_ORDER: Record<string, EulerOrder> = {
  XYZ: EulerOrder.XYZ,
  XZY: EulerOrder.XZY,
  YXZ: EulerOrder.YXZ,
  YZX: EulerOrder.YZX,
  ZXY: EulerOrder.ZXY,
  ZYX: EulerOrder.ZYX,
};

export type DecomposedTransform = {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  scale: THREE.Vector3;
};

/**
 * Cartographer rotation rule:
 * - quaternion is runtime/internal rotation truth
 * - Euler is used only for UI input/display
 */
export function toThreeEulerOrder(order: EulerOrder): THREE.EulerOrder {
  return CARTOGRAPHER_TO_THREE_EULER_ORDER[order] ?? 'XYZ';
}

export function parseEulerOrder(order: unknown): EulerOrder | null {
  if (typeof order !== 'string') return null;
  const normalized = order.trim().toUpperCase();
  return THREE_TO_CARTOGRAPHER_EULER_ORDER[normalized] ?? null;
}

export function normalizeQuaternion(quaternion: THREE.Quaternion): THREE.Quaternion {
  if (
    !Number.isFinite(quaternion.x) ||
    !Number.isFinite(quaternion.y) ||
    !Number.isFinite(quaternion.z) ||
    !Number.isFinite(quaternion.w)
  ) {
    quaternion.identity();
    return quaternion;
  }

  if (quaternion.lengthSq() <= 1e-12) {
    quaternion.identity();
    return quaternion;
  }

  quaternion.normalize();
  return quaternion;
}

export function quaternionFromEulerDegrees(
  eulerDeg: RotationAxisTriplet,
  order: EulerOrder,
): THREE.Quaternion {
  const euler = new THREE.Euler(
    THREE.MathUtils.degToRad(eulerDeg[0]),
    THREE.MathUtils.degToRad(eulerDeg[1]),
    THREE.MathUtils.degToRad(eulerDeg[2]),
    toThreeEulerOrder(order),
  );
  return normalizeQuaternion(new THREE.Quaternion().setFromEuler(euler));
}

export function eulerDegreesFromQuaternion(
  quaternion: THREE.Quaternion,
  order: EulerOrder,
): RotationAxisTriplet {
  const normalizedQuaternion = normalizeQuaternion(quaternion.clone());
  const euler = new THREE.Euler().setFromQuaternion(
    normalizedQuaternion,
    toThreeEulerOrder(order),
  );

  return [
    THREE.MathUtils.radToDeg(euler.x),
    THREE.MathUtils.radToDeg(euler.y),
    THREE.MathUtils.radToDeg(euler.z),
  ];
}

export function setRotationFromEulerDegrees(
  object: Pick<THREE.Object3D, 'quaternion'>,
  eulerDeg: RotationAxisTriplet,
  order: EulerOrder,
): void {
  object.quaternion.copy(quaternionFromEulerDegrees(eulerDeg, order));
}

export function getEulerDegreesFromQuaternion(
  object: Pick<THREE.Object3D, 'quaternion'>,
  order: EulerOrder,
): RotationAxisTriplet {
  return eulerDegreesFromQuaternion(object.quaternion, order);
}

export function applyLocalRotationOffset(
  object: Pick<THREE.Object3D, 'quaternion'>,
  deltaQuat: THREE.Quaternion,
): void {
  const normalizedDelta = normalizeQuaternion(deltaQuat.clone());
  object.quaternion.multiply(normalizedDelta);
  normalizeQuaternion(object.quaternion);
}

export type EulerAxisSequence = [
  'x' | 'y' | 'z',
  'x' | 'y' | 'z',
  'x' | 'y' | 'z',
];

const AXIS_VECTORS: Record<string, THREE.Vector3> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};

export function eulerAxisSequence(order: EulerOrder): EulerAxisSequence {
  return order.split('') as EulerAxisSequence;
}

export function buildGimbalQuaternions(
  posedWorldQuat: THREE.Quaternion,
  localQuat: THREE.Quaternion,
  order: EulerOrder,
): Record<'x' | 'y' | 'z', THREE.Quaternion> {
  const threeOrder = toThreeEulerOrder(order);
  const euler = new THREE.Euler().setFromQuaternion(localQuat, threeOrder);
  const axes = eulerAxisSequence(order);

  const Q = axes.map((a) =>
    new THREE.Quaternion().setFromAxisAngle(
      AXIS_VECTORS[a],
      euler[a],
    ),
  );

  const baseQuat = posedWorldQuat
    .clone()
    .multiply(localQuat.clone().invert());
  baseQuat.normalize();

  const result = {} as Record<'x' | 'y' | 'z', THREE.Quaternion>;

  result[axes[0]] = baseQuat.clone();

  result[axes[1]] = baseQuat.clone().multiply(Q[0]);
  result[axes[1]].normalize();

  result[axes[2]] = baseQuat.clone().multiply(Q[0]).multiply(Q[1]);
  result[axes[2]].normalize();

  return result;
}

const AXIS_IDX: Record<string, 0 | 1 | 2> = { x: 0, y: 1, z: 2 };

/**
 * Per-axis angular offset (degrees) that aligns ring angle coordinates with
 * the bone's actual child direction.  Without this offset, ring angle 0 for
 * each axis maps to a fixed reference direction (e.g. +Y for X-ring) which
 * may not coincide with where the bone visually points.
 *
 * The offset for axis at Euler-order position p is derived from the bone's
 * child direction rotated by the subsequent baseline Euler rotations,
 * projected onto that ring's plane.
 */
export function computeGimbalAngleOffsets(
  boneLocalChildDir: THREE.Vector3,
  baselineEulerDeg: [number, number, number],
  order: EulerOrder,
): Record<'x' | 'y' | 'z', number> {
  if (boneLocalChildDir.lengthSq() < 1e-12) {
    return { x: 0, y: 0, z: 0 };
  }

  const axes = eulerAxisSequence(order);
  const result: Record<'x' | 'y' | 'z', number> = { x: 0, y: 0, z: 0 };

  for (let p = 0; p < 3; p++) {
    const dir = boneLocalChildDir.clone();

    for (let i = 2; i > p; i--) {
      const a = axes[i] as 'x' | 'y' | 'z';
      dir.applyQuaternion(
        new THREE.Quaternion().setFromAxisAngle(
          AXIS_VECTORS[a],
          THREE.MathUtils.degToRad(baselineEulerDeg[AXIS_IDX[a]]),
        ),
      );
    }

    const key = axes[p] as 'x' | 'y' | 'z';
    switch (key) {
      case 'x':
        result.x = THREE.MathUtils.radToDeg(Math.atan2(dir.z, dir.y));
        break;
      case 'y':
        result.y = THREE.MathUtils.radToDeg(Math.atan2(dir.x, dir.z));
        break;
      case 'z':
        result.z = THREE.MathUtils.radToDeg(Math.atan2(dir.y, dir.x));
        break;
    }
  }

  return result;
}

export function decomposeMatrixToTransform(matrix: THREE.Matrix4): DecomposedTransform {
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  matrix.decompose(position, quaternion, scale);
  normalizeQuaternion(quaternion);
  return { position, quaternion, scale };
}

export function composeMatrixFromTransform(
  position: THREE.Vector3,
  quaternion: THREE.Quaternion,
  scale: THREE.Vector3,
): THREE.Matrix4 {
  const normalizedQuaternion = normalizeQuaternion(quaternion.clone());
  return new THREE.Matrix4().compose(
    position.clone(),
    normalizedQuaternion,
    scale.clone(),
  );
}
